import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../api';
import InfoTip from '../components/InfoTip';
import LogViewer from '../components/LogViewer';
import { useCrumbLabel } from '../components/shell/crumbs';
import { Icon, PageHeader } from '../components/ui';

// WS4: live and cold are collapsed into a single window. On (re)load we pull the last
// 3 days from CloudWatch (the "cold" store, which the fluentbit pipeline filters to
// WARN-level and worse) and then append the live pod stdout (which also carries
// INFO/DEBUG/TRACE, never shipped to CloudWatch). One scrollback: history, then live.
function UserLogs() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [logSource, setLogSource] = useState('openpaw'); // 'openpaw' (agent) | 'machine' (host)
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(true);
  const logViewerRef = useRef(null);
  // Numbers each load. The source switch stays usable while a load is in flight, so a
  // slow answer for the source just left must not overwrite the one just picked.
  const loadSeq = useRef(0);

  useCrumbLabel(user?.username);

  // Fetch user info
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await apiFetch(`/api/users/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, [userId]);

  // Read a log endpoint and return its text, swallowing errors into a marker line.
  const readLog = async (endpoint) => {
    try {
      const response = await apiFetch(endpoint);
      const data = await response.json();
      return data.logs || data.message || data.detail || '';
    } catch (error) {
      return '';
    }
  };

  // Load the combined window: cold (CloudWatch, last 3d errors) then live (stdout).
  // The `────` lines are section fences; LogViewer draws them as dividers.
  const fetchLogs = async (source = logSource) => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLogsLoading(true);
    // Cold archive — warnings+errors retained 3 days in CloudWatch, survives hibernation.
    const cold = await readLog(`/api/containers/${userId}/logs?source=cold&tail=500`);
    // Live — running pod stdout (runtime) or host machine, depending on the source toggle.
    const liveEndpoint = source === 'openpaw'
      ? `/api/containers/${userId}/logs?source=live&tail=500`
      : `/api/containers/${userId}/machine-logs?tail=500`;
    const live = await readLog(liveEndpoint);
    if (seq !== loadSeq.current) return;

    const parts = [];
    if (cold && cold.trim()) {
      parts.push('──────── CloudWatch archive · last 3 days (warnings + errors) ────────');
      parts.push(cold.trimEnd());
    }
    parts.push('──────── live ────────');
    parts.push(live && live.trim() ? live.trimEnd() : 'No live logs available');
    setLogsContent(parts.join('\n'));
    setLogsLoading(false);
  };

  // Initial fetch and when source/user changes
  useEffect(() => {
    fetchLogs(logSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, logSource]);

  // Handle source change (agent/host) for the live portion
  const handleSourceChange = (value) => {
    setLogSource(value);
  };

  const name = user?.username || `User ${userId}`;

  return (
    <div>
      <PageHeader
        // The trail is the way back on phones, where the top bar shows only the last crumb.
        eyebrow={(
          <>
            <Link to="/users">Users</Link>
            <Icon name="chevronRight" size={12} />
            <Link to={`/users/${userId}`}>{name}</Link>
          </>
        )}
        description="Recent output from this agent, with the newest lines at the bottom."
        actions={(
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fetchLogs(logSource)}
            disabled={logsLoading}
          >
            {logsLoading ? <><span className="spinner-sm" /> Loading…</> : <><Icon name="refresh" /> Refresh</>}
          </button>
        )}
      >
        <h1 className="page-title">
          Logs
          <InfoTip label="What these logs cover" width={340}>
            Three days of archived warnings and errors from CloudWatch, then the live
            stdout stream. A healthy agent logs nothing at WARN or above, so an empty
            archive is the normal case rather than a gap.
          </InfoTip>
        </h1>
      </PageHeader>

      <div className="card">
        <LogViewer
          ref={logViewerRef}
          logs={logsContent}
          loading={logsLoading}
          scrollToBottom={true}
          logSource={logSource}
          onSourceChange={handleSourceChange}
        />
      </div>
    </div>
  );
}

export default UserLogs;
